import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courtsApi, Court } from '../api/courts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

function CourtCard({ court, onEdit }: { court: Court; onEdit?: (c: Court) => void }) {
  const { t } = useTranslation();
  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-lg">{court.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {court.address}, {court.zip_code} {court.city}
            {court.country && court.country !== 'Deutschland' && (
              <span className="ml-1 text-gray-400">· {court.country}</span>
            )}
          </p>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
            <span>{court.courts_count} {t('courts.courtsCount')}</span>
            {court.price_per_hour && <span>{court.price_per_hour} €/h</span>}
            {court.booking_url && (
              <a href={court.booking_url} target="_blank" rel="noopener noreferrer"
                className="text-primary-600 hover:underline font-medium">
                {t('courts.book')} →
              </a>
            )}
            {court.padelmap_url && (
              <a href={court.padelmap_url} target="_blank" rel="noopener noreferrer"
                className="text-orange-600 hover:underline font-medium">
                padelmap.app →
              </a>
            )}
          </div>
        </div>
        {onEdit && (
          <button onClick={() => onEdit(court)} className="btn btn-secondary btn-sm shrink-0">
            {t('courts.edit')}
          </button>
        )}
      </div>
    </div>
  );
}

function sanitize(form: Record<string, unknown>): Record<string, unknown> {
  return {
    ...form,
    booking_url: form.booking_url || null,
    padelmap_url: form.padelmap_url || null,
    price_per_hour: form.price_per_hour !== '' ? form.price_per_hour : null,
  };
}

function CourtForm({ initial, onSave, onCancel, error, isPending }: {
  initial?: Partial<Court>;
  onSave: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  error?: string;
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    address: initial?.address ?? '',
    city: initial?.city ?? '',
    zip_code: initial?.zip_code ?? '',
    country: initial?.country ?? 'Deutschland',
    courts_count: initial?.courts_count ?? 1,
    booking_url: initial?.booking_url ?? '',
    padelmap_url: initial?.padelmap_url ?? '',
    price_per_hour: initial?.price_per_hour ?? '',
  });

  function set(k: string, v: unknown) { setForm(f => ({ ...f, [k]: v })); }

  const canSave = !!form.name && !!form.address && !!form.city && !!form.zip_code;

  return (
    <div className="card border-2 border-primary-200">
      <h3 className="font-semibold text-gray-900 mb-4">{initial?.id ? t('courts.editTitle') : t('courts.addTitle')}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label">{t('courts.name')} *</label>
          <input className="input" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">{t('courts.address')} *</label>
          <input className="input" value={form.address} onChange={e => set('address', e.target.value)} />
        </div>
        <div>
          <label className="label">{t('courts.city')} *</label>
          <input className="input" value={form.city} onChange={e => set('city', e.target.value)} />
        </div>
        <div>
          <label className="label">{t('courts.zipCode')} *</label>
          <input className="input" value={form.zip_code} onChange={e => set('zip_code', e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">{t('courts.country')} *</label>
          <select className="input" value={form.country} onChange={e => set('country', e.target.value)}>
            <option>Deutschland</option>
            <option>Dänemark</option>
            <option>Österreich</option>
            <option>Schweiz</option>
            <option>Niederlande</option>
            <option>Belgien</option>
            <option>Frankreich</option>
            <option>Spanien</option>
          </select>
        </div>
        <div>
          <label className="label">{t('courts.courtsCountLabel')}</label>
          <input className="input" type="number" min={1} value={form.courts_count}
            onChange={e => set('courts_count', parseInt(e.target.value))} />
        </div>
        <div>
          <label className="label">{t('courts.pricePerHour')}</label>
          <input className="input" type="number" step="0.5" value={form.price_per_hour}
            onChange={e => set('price_per_hour', e.target.value ? parseFloat(e.target.value) : '')} />
        </div>
        <div>
          <label className="label">{t('courts.bookingUrl')}</label>
          <input className="input" type="url" value={form.booking_url}
            onChange={e => set('booking_url', e.target.value)} placeholder="https://..." />
        </div>
        <div>
          <label className="label">{t('courts.padelmapUrl')}</label>
          <input className="input" type="url" value={form.padelmap_url}
            onChange={e => set('padelmap_url', e.target.value)} placeholder="https://padelmap.app/venue/..." />
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm mt-3">
          {error}
        </div>
      )}
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => onSave(sanitize(form as Record<string, unknown>))}
          disabled={!canSave || isPending}
          className="btn btn-primary btn-sm"
        >
          {isPending ? '...' : t('courts.save')}
        </button>
        <button onClick={onCancel} className="btn btn-secondary btn-sm">{t('admin.cancel')}</button>
      </div>
    </div>
  );
}

export default function Courts() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [cityFilter, setCityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editCourt, setEditCourt] = useState<Court | null>(null);
  const [createError, setCreateError] = useState('');
  const [updateError, setUpdateError] = useState('');

  const { data: courts = [], isLoading } = useQuery({
    queryKey: ['courts', cityFilter],
    queryFn: () => courtsApi.list(cityFilter || undefined).then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => courtsApi.create(d as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courts'] });
      setShowForm(false);
      setCreateError('');
    },
    onError: (e: any) => setCreateError(e.response?.data?.detail ?? 'Fehler beim Speichern'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, d }: { id: number; d: Record<string, unknown> }) => courtsApi.update(id, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courts'] });
      setEditCourt(null);
      setUpdateError('');
    },
    onError: (e: any) => setUpdateError(e.response?.data?.detail ?? 'Fehler beim Speichern'),
  });

  const cities = Array.from(new Set(courts.map(c => c.city))).sort();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('courts.title')}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{t('courts.subtitle')}</p>
        </div>
        {isAdmin && !showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
            + {t('courts.add')}
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setCityFilter('')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!cityFilter ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
          {t('leagueList.filterAll')}
        </button>
        {cities.map(city => (
          <button key={city} onClick={() => setCityFilter(city)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${cityFilter === city ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {city}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="mb-6">
          <CourtForm
            onSave={d => { setCreateError(''); createMut.mutate(d); }}
            onCancel={() => { setShowForm(false); setCreateError(''); }}
            error={createError}
            isPending={createMut.isPending}
          />
        </div>
      )}

      {editCourt && (
        <div className="mb-6">
          <CourtForm
            initial={editCourt}
            onSave={d => { setUpdateError(''); updateMut.mutate({ id: editCourt.id, d }); }}
            onCancel={() => { setEditCourt(null); setUpdateError(''); }}
            error={updateError}
            isPending={updateMut.isPending}
          />
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="card animate-pulse h-24" />)}</div>
      ) : courts.length === 0 ? (
        <div className="text-center py-16 text-gray-500">{t('courts.empty')}</div>
      ) : (
        <div className="space-y-4">
          {courts.map(c => (
            <CourtCard key={c.id} court={c} onEdit={isAdmin ? setEditCourt : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}
